<template>
    <a
        class="btn"
        :class="[
            `btn--${resolvedVariant}`,
            `btn--${resolvedSize}`,
            { 'btn--gradient': resolvedGradient, 'btn--full': fullWidth, 'disabled': disabled }
        ]"
        @click="$emit('click')"
    >
        <span v-if="loading" class="btn__spinner" aria-label="loading"></span>
        <slot v-else></slot>
    </a>
</template>

<script>
// myREST design-system button.
// New API: variant {primary|secondary|ghost|danger|neutral}, size {sm|md|lg},
// gradient (the signature brand-gradient CTA), fullWidth.
// Legacy API (type/height) is still accepted and mapped, so existing call sites
// keep working until each screen is migrated to the explicit props.
const LEGACY_VARIANT = { violet: 'primary', 'dark-violet': 'primary', bordered: 'ghost' };
const LEGACY_SIZE = { tall: 'lg', short: 'md' };

export default {
    name: "VButton",
    props: {
        // legacy
        type: { type: String, default: 'violet' },
        height: { type: String, default: 'tall' },
        // new
        variant: { type: String, default: null },
        size: { type: String, default: null },
        gradient: { type: Boolean, default: null },
        fullWidth: { type: Boolean, default: false },
        // shared
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false }
    },
    computed: {
        resolvedVariant() {
            return this.variant || LEGACY_VARIANT[this.type] || 'primary';
        },
        resolvedSize() {
            return this.size || LEGACY_SIZE[this.height] || 'md';
        },
        resolvedGradient() {
            if (this.gradient !== null) return this.gradient;
            // explicit new variant → opt-in gradient only; legacy CTAs are gradient
            if (this.variant !== null) return false;
            return this.type === 'violet' || this.type === 'dark-violet';
        }
    }
}
</script>

<style lang="scss" scoped>
.btn {
    box-sizing: border-box;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 9px;
    width: auto;
    border-radius: var(--radius-xl);
    border: none;
    font-family: var(--font-sans);
    font-size: var(--text-md);
    font-weight: var(--weight-bold);
    line-height: 1;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    transition: transform var(--dur-fast) var(--ease-standard),
                background var(--dur-base) var(--ease-standard),
                box-shadow var(--dur-base) var(--ease-standard),
                filter var(--dur-base) var(--ease-standard);

    &:active { transform: scale(0.97); }

    &.disabled {
        opacity: 0.45;
        pointer-events: none;
    }

    &.btn--full { width: 100%; }
}

/* sizes */
.btn--sm { height: 40px; padding: 0 16px; border-radius: var(--radius-lg); font-size: var(--text-base); }
.btn--md { height: 50px; padding: 0 22px; }
.btn--lg { height: 60px; padding: 0 28px; gap: 10px; }

/* variants */
.btn--primary {
    background: var(--color-primary);
    color: var(--color-on-primary);
    box-shadow: var(--shadow-sm);

    &.btn--gradient {
        background: var(--grad-brand);
        box-shadow: var(--shadow-brand);
    }
}

.btn--secondary {
    background: var(--color-primary-soft);
    color: var(--color-primary);
}

.btn--ghost {
    background: transparent;
    color: var(--color-primary);
    border: 2px solid var(--color-primary);
}

.btn--danger {
    background: var(--danger);
    color: #fff;
}

.btn--neutral {
    background: var(--surface-soft);
    color: var(--text-primary);
}

/* spinner */
.btn__spinner {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2.5px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    animation: btn-spin 0.7s linear infinite;
}

@keyframes btn-spin {
    to { transform: rotate(360deg); }
}
</style>
